import { useEffect, useMemo, useState } from "react";

import { useParams, useSearchParams } from "react-router-dom";

import {
  ChartBarSquareIcon,
  GlobeEuropeAfricaIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

import sortOffers from "../../utils/sortOffers";
import classNames from "../../utils/classNames";
import Card from "../../components/shared/Card/Card";
import usePartInvoice from "../../data/usePartInvoice";
import useSinglePart from "../../data/useSinglePart";
import PartQc from "../../components/leads/LeadParts/PartQc";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import StockVehicleParts from "../../components/stock/viewStock/StockVehicleParts";
import EditListingImages from "../../components/ebayListings/EditListingImages";
import StockVehicleDetails from "../../components/stock/viewStock/StockVehicleDetails";
import EbayPartMessages from "../../components/shared/EbayPartMessages/EbayPartMessages";
import LinkedWorkOrdersCard from "../../components/workOrders/LinkedWorkOrdersCard";
import { SearchPaletteItem } from "../../components/shared/Inputs/SearchPaletteTypes";
import { IPart, PartStatus } from "../../models/Part";

import EditListing from "../../components/ebayListings/EditListing";
import StockHeader from "../../components/stock/viewStock/StockHeader";
import StockOverview from "../../components/stock/viewStock/StockOverview";
import StockTimeline from "../../components/stock/viewStock/StockTimeline";
import StockQuotes from "../../components/stock/viewStock/StockQuotes";
import StockSalesInformation from "../../components/stock/viewStock/StockSalesInformation";
import StockFitment from "../../components/stock/viewStock/StockFitment";
import StockNotes from "../../components/stock/viewStock/StockNotes";
import StockIntelligence from "../../components/stock/viewStock/StockIntelligence";
import StockVehicleIntelligence from "../../components/stock/viewStock/StockVehicleIntelligence";
import StockSustainability from "../../components/stock/viewStock/StockSustainability";

type TabId =
  | "overview"
  | "intelligence"
  | "sustainability"
  | "images"
  | "ebayListing"
  | "timeline"
  | "vehicle"
  | "vehicleParts"
  | "vehicleIntelligence"
  | "fitment"
  | "workOrders"
  | "notes"
  | "ebayMessages"
  | "quotes"
  | "sales"
  | "qc";

interface TabDef {
  id: TabId;
  label: string;
  disabled?: boolean;
  hidden?: boolean;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** When true, renders the tab with a gradient/sparkly accent. */
  fancy?: boolean;
}

const TAB_IDS: TabId[] = [
  "overview",
  "intelligence",
  "sustainability",
  "images",
  "ebayListing",
  "timeline",
  "vehicle",
  "vehicleParts",
  "vehicleIntelligence",
  "fitment",
  "workOrders",
  "notes",
  "ebayMessages",
  "quotes",
  "sales",
  "qc",
];

const isTabId = (value: string | null): value is TabId =>
  value !== null && (TAB_IDS as string[]).includes(value);

export default function ViewStock() {
  const { setInfo } = usePageNameContext();
  const { partId } = useParams();
  const { singlePart } = useSinglePart(partId!);
  const { invoice } = usePartInvoice(partId!);

  // Allow other pages (e.g. Work Orders, expanded rows) to deep-link directly
  // to a specific tab via `?tab=<tabId>`. Keeps the URL shareable and falls
  // back silently if the id is unknown or disabled.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const initialTab: TabId = isTabId(requestedTab) ? requestedTab : "overview";

  const [part, setPart] = useState<IPart | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [ebayListingPage, setEbayListingPage] = useState<number>(0);

  useEffect(() => {
    setInfo({
      name: "Stock Details",
      desc: "View details of this part here",
    });
  }, [setInfo]);

  // Keep the active tab and the `?tab=` query param in sync so the current
  // view is shareable/bookmarkable. We strip the param for the default tab
  // to keep the URL tidy.
  useEffect(() => {
    if (isTabId(requestedTab) && requestedTab !== activeTab) {
      setActiveTab(requestedTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab]);

  useEffect(() => {
    const current = searchParams.get("tab");
    if (activeTab === "overview") {
      if (current !== null) {
        const next = new URLSearchParams(searchParams);
        next.delete("tab");
        setSearchParams(next, { replace: true });
      }
      return;
    }
    if (current === activeTab) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", activeTab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (!singlePart.isLoading && singlePart.data?.data) {
      const next = singlePart.data.data;

      if (next.offers && next.offers.length > 0) {
        next.offers.sort(sortOffers);
      }

      if (next.images && next.images.length > 0) {
        next.images.sort((a, b) => {
          if (a.order === null) return 1;
          if (b.order === null) return -1;
          return a.order - b.order;
        });
      }

      setPart(next);
      setIsLoading(false);
    }
  }, [partId, singlePart.data, singlePart.isLoading]);

  const workOrderItems = useMemo<SearchPaletteItem[]>(() => {
    if (!part?.id) return [];
    const isSoldOrder = part.partStatus === PartStatus.Sold;
    return [
      {
        id: part.id,
        name: `${part.tagNumber ?? "Unknown Tag"} - ${part.title ?? "Part"}`,
        type: isSoldOrder ? "Order" : "Stock",
      },
    ];
  }, [part]);

  const tabs = useMemo<TabDef[]>(() => {
    if (!part) return [];
    const hasInvoice = !!invoice?.data?.data;
    const hasSale = !!part.soldDate || hasInvoice;
    const hasVehicle = !!part.vehicle;
    return [
      { id: "overview", label: "Overview" },
      {
        id: "intelligence",
        label: "Intelligence",
        icon: SparklesIcon,
        fancy: true,
      },
      {
        id: "sustainability",
        label: "Sustainability",
        icon: GlobeEuropeAfricaIcon,
      },
      { id: "images", label: "Images" },
      { id: "ebayListing", label: "eBay Listing" },
      { id: "timeline", label: "Timeline" },
      { id: "vehicle", label: "Vehicle", disabled: !hasVehicle },
      { id: "vehicleParts", label: "Other Parts", disabled: !part.vehicleId },
      {
        id: "vehicleIntelligence",
        label: "Vehicle Stock Intelligence",
        icon: ChartBarSquareIcon,
        fancy: true,
        disabled: !hasVehicle,
      },
      { id: "fitment", label: "Fitment" },
      { id: "workOrders", label: "Work Orders" },
      { id: "notes", label: "Notes" },
      {
        id: "ebayMessages",
        label: "eBay Messages",
        disabled: !part.ebayItemNumber,
      },
      { id: "quotes", label: "Quotes" },
      { id: "sales", label: "Sales Information", disabled: !hasSale },
      { id: "qc", label: "QC", hidden: part.partStatus !== PartStatus.QC },
    ];
  }, [part, invoice?.data?.data]);

  if (isLoading || !part) {
    return <LoadingWheel />;
  }

  const visibleTabs = tabs.filter((t) => !t.hidden);

  return (
    <div className="mt-4 flex flex-col gap-6">
      <StockHeader part={part} invoice={invoice?.data?.data ?? null} />

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex gap-4 sm:gap-8 overflow-x-auto" aria-label="Tabs">
              {visibleTabs.map((tab) => {
                const isActive = tab.id === activeTab;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    disabled={tab.disabled}
                    onClick={() => setActiveTab(tab.id)}
                    className={classNames(
                      "group relative inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors",
                      tab.disabled
                        ? "border-transparent text-gray-300 cursor-not-allowed"
                        : tab.fancy && isActive
                          ? "border-violet-500 text-violet-700"
                          : tab.fancy
                            ? "border-transparent text-violet-600 hover:text-violet-700 hover:border-violet-300"
                            : isActive
                              ? "border-primary-600 text-primary-700"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    )}
                  >
                    {Icon && (
                      <Icon
                        className={classNames(
                          "h-4 w-4 transition-transform",
                          tab.fancy && !tab.disabled
                            ? "text-violet-500 group-hover:scale-110"
                            : "",
                          tab.fancy && !tab.disabled && isActive
                            ? "drop-shadow-[0_0_6px_rgba(168,85,247,0.55)]"
                            : ""
                        )}
                        aria-hidden="true"
                      />
                    )}
                    {tab.fancy && !tab.disabled ? (
                      <span className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-sky-500 bg-clip-text text-transparent">
                        {tab.label}
                      </span>
                    ) : (
                      tab.label
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          <div>
            {activeTab === "overview" && <StockOverview part={part} />}
            {activeTab === "intelligence" && <StockIntelligence part={part} />}
            {activeTab === "sustainability" && (
              <StockSustainability part={part} />
            )}
            {activeTab === "images" && <EditListingImages part={part} />}
            {activeTab === "ebayListing" && (
              <EditListing
                part={part}
                page={ebayListingPage}
                setPage={setEbayListingPage}
              />
            )}
            {activeTab === "timeline" && <StockTimeline part={part} />}
            {activeTab === "vehicle" &&
              (part.vehicle ? (
                <StockVehicleDetails vehicle={part.vehicle} />
              ) : (
                <Card bodyClassName="p-6 text-sm text-gray-500">
                  No linked vehicle
                </Card>
              ))}
            {activeTab === "vehicleParts" &&
              (part.vehicleId ? (
                <StockVehicleParts
                  vehicleId={part.vehicleId}
                  currentPartId={part.id}
                />
              ) : (
                <Card bodyClassName="p-6 text-sm text-gray-500">
                  No linked vehicle
                </Card>
              ))}
            {activeTab === "vehicleIntelligence" &&
              (part.vehicle ? (
                <StockVehicleIntelligence part={part} />
              ) : (
                <Card bodyClassName="p-6 text-sm text-gray-500">
                  No linked vehicle
                </Card>
              ))}
            {activeTab === "fitment" && <StockFitment part={part} />}
            {activeTab === "workOrders" && (
              <LinkedWorkOrdersCard linkedItems={workOrderItems} />
            )}
            {activeTab === "notes" && <StockNotes part={part} />}
            {activeTab === "ebayMessages" && <EbayPartMessages part={part} />}
            {activeTab === "quotes" && <StockQuotes part={part} />}
            {activeTab === "sales" && (
              <StockSalesInformation
                part={part}
                invoice={invoice?.data?.data ?? null}
              />
            )}
            {activeTab === "qc" && part.partStatus === PartStatus.QC && (
              <PartQc part={part} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
